import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Check, X, Repeat, ShoppingBasket, CheckCircle2, Store, User, Filter } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ScanBar, EmptyState } from "./fxui";

const SHOP_PILL = {
  pending: { label: "To buy", cls: "bg-black/5 text-[#5C5C5C]" },
  picked: { label: "Bought", cls: "bg-[#306D29] text-white" },
  partial: { label: "Bought (part)", cls: "bg-[#D9933B] text-white" },
  substitute: { label: "Substituted", cls: "bg-[#2563EB] text-white" },
  not_found: { label: "Not available", cls: "bg-[#B23B3B] text-white" },
  wrong_item: { label: "Wrong item", cls: "bg-[#B23B3B] text-white" },
  damaged: { label: "Damaged", cls: "bg-[#B23B3B] text-white" },
  on_hold: { label: "On hold", cls: "bg-black/50 text-white" },
};
const NO_STORE = "Store not set";

export default function FxShopping() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ to_buy: 0, bought: 0, orders: 0, cost: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("customer");   // customer | store
  const [storeFilter, setStoreFilter] = useState("");
  const [onlyToBuy, setOnlyToBuy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/shopping"); setOrders(data.orders || []); setSummary(data.summary || {}); }
    catch { toast.error("Could not load the shopping list"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = async (onum, key, status, extra = {}) => {
    try { await api.post(`/fx/pick/${onum}/item`, { key, pick_status: status, ...extra }); await load(); }
    catch { toast.error("Could not update item"); }
  };
  const buy = (onum, key, qty) => mark(onum, key, "picked", { qty_found: qty });
  const unavailable = (onum, key) => mark(onum, key, "not_found", { exception_reason: "Not available" });
  const substitute = (onum, key) => { const n = window.prompt("What did you buy instead?"); if (n) mark(onum, key, "substitute", { substitute_note: n }); };
  const savePrice = (onum, key, status, price) => mark(onum, key, status || "pending", { price_paid: price });
  const saveStore = (onum, key, status, store) => mark(onum, key, status || "pending", { source_store: store });
  const finishOrder = async (onum) => {
    try { await api.post(`/fx/pick/${onum}/complete`); toast.success("Order marked fully bought — sent to packing"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Some items still to buy"); }
  };
  const scan = async (code) => {
    const c = code.trim();
    if (c.includes("~")) { const [onum, key] = c.split("~"); await buy(onum, key, 1); toast.success("Marked bought"); }
    else toast.message("Scan an item barcode to mark it bought");
  };

  // flatten with order context
  const allItems = orders.flatMap((o) => o.items.map((it) => ({ ...it, order_number: o.order_number, customer_name: o.customer_name, city: o.city })));
  const stores = Array.from(new Set(allItems.map((it) => it.source_store || NO_STORE))).sort();
  const matches = (it) => (!onlyToBuy || it.status === "pending") && (!storeFilter || (it.source_store || NO_STORE) === storeFilter);

  const itemControls = (it, showCustomer) => {
    const pill = SHOP_PILL[it.status] || SHOP_PILL.pending;
    return (
      <div key={`${it.order_number}-${it.key}`} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`fx-shop-item-${it.order_number}-${it.key}`}>
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-medium">{it.name} <span className="text-xs text-[#5C5C5C]">× {it.quantity}</span></p>
          <p className="text-xs text-[#5C5C5C]">
            {showCustomer ? `${it.customer_name} · ${it.order_number}` : (it.source_store || NO_STORE)}
            {it.substitute_note ? ` · sub: ${it.substitute_note}` : ""}
          </p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${pill.cls}`}>{pill.label}</span>
        <StoreInput value={it.source_store} onSave={(s) => saveStore(it.order_number, it.key, it.status, s)} testid={`fx-shop-store-${it.order_number}-${it.key}`} />
        <PriceInput value={it.price_paid} onSave={(p) => savePrice(it.order_number, it.key, it.status, p)} testid={`fx-shop-price-${it.order_number}-${it.key}`} />
        <div className="flex gap-2">
          <button onClick={() => buy(it.order_number, it.key, it.quantity)} data-testid={`fx-shop-buy-${it.order_number}-${it.key}`}
            className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full ${it.status === "picked" ? "bg-[#306D29] text-white" : "bg-black/5 text-[#306D29] hover:bg-[#306D29]/10"}`}><Check size={13} /> Bought</button>
          <button onClick={() => substitute(it.order_number, it.key)} data-testid={`fx-shop-sub-${it.order_number}-${it.key}`}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-black/5 text-[#2563EB] hover:bg-[#2563EB]/10"><Repeat size={13} /> Sub</button>
          <button onClick={() => unavailable(it.order_number, it.key)} data-testid={`fx-shop-na-${it.order_number}-${it.key}`}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-black/5 text-[#B23B3B] hover:bg-[#B23B3B]/10"><X size={13} /> N/A</button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="fx-shopping">
      <ScanBar onSubmit={scan} placeholder="Scan an item to mark it bought…" testid="fx-shopping-scan" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat value={summary.to_buy} label="items to buy" tone="#D9933B" />
        <Stat value={summary.bought} label="bought" tone="#306D29" />
        <Stat value={`£${(summary.cost || 0).toLocaleString()}`} label="spent so far" tone="#0D530E" />
        <Stat value={summary.orders} label="orders shopping" tone="#0D530E" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="inline-flex bg-white border border-black/10 rounded-full p-1">
          <button onClick={() => setView("customer")} data-testid="fx-shop-view-customer"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${view === "customer" ? "bg-[#306D29] text-white" : "text-[#5C5C5C]"}`}><User size={13} /> By customer</button>
          <button onClick={() => setView("store")} data-testid="fx-shop-view-store"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${view === "store" ? "bg-[#306D29] text-white" : "text-[#5C5C5C]"}`}><Store size={13} /> By store</button>
        </div>
        <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} data-testid="fx-shop-store-filter"
          className="bg-white border border-black/10 rounded-full px-4 py-2 text-sm">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setOnlyToBuy((v) => !v)} data-testid="fx-shop-only-tobuy"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border ${onlyToBuy ? "bg-[#306D29] text-white border-[#306D29]" : "bg-white text-[#306D29] border-black/10"}`}><Filter size={14} /> Only to buy</button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : orders.length === 0 ? <EmptyState>🛒 Nothing to buy right now. New paid orders will appear here.</EmptyState>
        : view === "customer" ? (
        <div className="space-y-5">
          {orders.map((o) => {
            const items = o.items.filter(matches);
            if (items.length === 0) return null;
            const allDone = o.done === o.total && o.total > 0;
            return (
              <div key={o.order_number} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`fx-shop-order-${o.order_number}`}>
                <div className="flex flex-wrap items-center gap-3 px-5 py-4 bg-[#0D530E] text-[#FBF5DD]">
                  <ShoppingBasket size={18} />
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-semibold">{o.customer_name}</p>
                    <p className="text-xs text-[#FBF5DD]/75">{o.order_number} · {o.city || "—"}</p>
                  </div>
                  <span className="text-sm font-semibold">{o.done} of {o.total} bought</span>
                  {allDone && !o.pick_complete && (
                    <button onClick={() => finishOrder(o.order_number)} data-testid={`fx-shop-finish-${o.order_number}`}
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#E7E1B1] text-[#0D530E]"><CheckCircle2 size={14} /> Done shopping</button>
                  )}
                  {o.pick_complete && <span className="text-xs font-semibold flex items-center gap-1 text-[#E7E1B1]"><CheckCircle2 size={14} /> Sent to packing</span>}
                </div>
                <div className="divide-y divide-black/5">{items.map((it) => itemControls(it, false))}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-5">
          {stores.map((s) => {
            const items = allItems.filter((it) => (it.source_store || NO_STORE) === s).filter(matches);
            if (items.length === 0) return null;
            return (
              <div key={s} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`fx-shop-store-${s}`}>
                <div className="flex items-center gap-3 px-5 py-4 bg-[#306D29] text-[#FBF5DD]">
                  <Store size={18} />
                  <p className="font-semibold flex-1">{s}</p>
                  <span className="text-sm font-semibold">{items.length} item(s)</span>
                </div>
                <div className="divide-y divide-black/5">{items.map((it) => itemControls(it, true))}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ value, label, tone }) {
  return <div className="bg-white border border-black/5 rounded-2xl p-4"><p className="font-display text-4xl" style={{ color: tone }}>{value}</p><p className="text-xs text-[#5C5C5C] mt-1">{label}</p></div>;
}

function PriceInput({ value, onSave, testid }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = () => { const cur = value ?? ""; if (String(v) !== String(cur)) onSave(v === "" ? null : Number(v)); };
  return (
    <div className="relative">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#5C5C5C]">£</span>
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        type="number" min="0" step="0.01" placeholder="paid" data-testid={testid}
        className="w-24 bg-white border border-black/10 rounded-full pl-6 pr-2 py-1.5 text-xs focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
    </div>
  );
}

function StoreInput({ value, onSave, testid }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => { if ((v || "") !== (value || "")) onSave(v.trim()); };
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      placeholder="store" data-testid={testid} title="Set the shop for this item"
      className="w-28 bg-white border border-black/10 rounded-full px-3 py-1.5 text-xs focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
  );
}
